import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuthRedirectUrl, supabase } from '@/lib/supabase';
import { Mail, Lock, AlertCircle } from 'lucide-react';

const REFERRAL_STORAGE_KEY = 'referral_code';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';

const getAuthErrorMessage = (err) => {
  const msg = String(err?.message || '').toLowerCase();
  if (msg.includes('invalid login credentials')) return 'Invalid email or password. Please check and try again.';
  if (msg.includes('email not confirmed')) return 'Please verify your email first, then sign in.';
  if (msg.includes('too many requests')) return 'Too many attempts. Please wait a moment and try again.';
  if (msg.includes('network')) return 'Network issue detected. Check your connection and try again.';
  return err?.message || 'Unable to sign in right now. Please try again.';
};

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref && ref.trim()) {
      try {
        sessionStorage.setItem(REFERRAL_STORAGE_KEY, ref.trim().toUpperCase());
      } catch (_) {}
    }
  }, [searchParams]);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setInfo(null);

    try {
      if (!email || !password) {
        throw new Error('Please enter email and password');
      }

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) throw signInError;

      if (data.user) {
        // Route guards decide dashboard vs onboarding after auth hydration.
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      console.error('[Auth/Login] Sign in failed', {
        message: err?.message,
        code: err?.code,
        status: err?.status,
        name: err?.name,
      });
      setError(getAuthErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address to reset password');
      return;
    }

    setIsLoading(true);
    setError(null);
    setInfo(null);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: getAuthRedirectUrl('/reset-password'),
      });

      if (resetError) throw resetError;

      setInfo('Password reset link sent. Please check your inbox.');
    } catch (err) {
      console.error('[Auth/Login] Reset password failed', {
        message: err?.message,
        code: err?.code,
        status: err?.status,
      });
      setError(getAuthErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Caterer Sign In</h1>
          <p className="text-gray-600">Sign in to your Mrs Cater caterer account.</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
        {info && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-sm text-emerald-800">{info}</p>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="pl-10"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-10"
                required
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Forgot password?
            </button>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full"
            size="lg"
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            <button
              onClick={() => navigate('/')}
              className="text-primary-600 hover:text-primary-700 font-medium mr-1"
            >
              Back to landing
            </button>
            · {' '}
            Don't have an account?{' '}
            <button
              onClick={() => navigate('/onboarding')}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Activate profile – Sign up
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
